import React, { useContext, useMemo } from 'react';
import type { Schema } from 'hast-util-sanitize';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import 'katex/dist/katex.min.css';
import type { CodeRef } from './CodeReferencesPanel';
import { getCodeReferenceExternalUrl, injectCodeMarkers } from './CodeReferencesPanel';
import { CodeReferencesUiContext } from './codeReferencesContext';
import { TooltipContent, TooltipTrigger } from '../ui/tooltip';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

/**
 * Extend GitHub-style sanitization so rehype-katex output survives (clinical vitals often use $\\text{}$ LaTeX).
 * Model output already flows through clinician-only surfaces; spans need classNames for KaTeX layout.
 */
export const clinicalMarkdownSanitizeSchema: Schema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames ?? []),
    'svg',
    'path',
    'line',
    'g',
    'circle',
    'rect',
    'math',
    'semantics',
    'mrow',
    'mi',
    'mo',
    'mn',
    'msup',
    'msub',
    'mspace',
    'mtext',
    'annotation',
  ],
  attributes: {
    ...defaultSchema.attributes,
    svg: [...(defaultSchema.attributes?.svg ?? []), 'width', 'height', 'viewBox', 'focusable', 'xmlns', 'preserveAspectRatio', 'xmlnsXlink'],
    path: [...(defaultSchema.attributes?.path ?? [])],
    math: [...(defaultSchema.attributes?.math ?? [])],
    semantics: [...(defaultSchema.attributes?.semantics ?? [])],
    annotation: [...(defaultSchema.attributes?.annotation ?? [])],
    span: [
      ...(defaultSchema.attributes?.span ? [...defaultSchema.attributes.span] : []),
      'className',
      'aria-hidden',
      'aria-label',
    ],
  },
};

/** Inline annotations written by the model, e.g. (RxNorm: 1191) or (LOINC: 2345-7). */
export const INLINE_CODE_RE = /\((ICD10|ICD-10|ICD-10-CM|RXNORM|LOINC|CPT|NDC|SNOMED|SNOMED\s*CT):\s*([A-Z0-9][\w.-]{1,22})\)/gi;

// Non-clinical categorical tagging (which coding system, not severity) —
// --chart-categorical-* tokens, one per system, never --band-*.
const INLINE_CODE_COLORS: Record<string, string> = {
  ICD10: 'bg-(--chart-categorical-1)/10 text-(--chart-categorical-1) ring-(--chart-categorical-1)/20',
  'ICD-10': 'bg-(--chart-categorical-1)/10 text-(--chart-categorical-1) ring-(--chart-categorical-1)/20',
  'ICD-10-CM': 'bg-(--chart-categorical-1)/10 text-(--chart-categorical-1) ring-(--chart-categorical-1)/20',
  RXNORM: 'bg-(--chart-categorical-2)/10 text-(--chart-categorical-2) ring-(--chart-categorical-2)/20',
  LOINC: 'bg-(--chart-categorical-3)/10 text-(--chart-categorical-3) ring-(--chart-categorical-3)/20',
  CPT: 'bg-(--chart-categorical-4)/10 text-(--chart-categorical-4) ring-(--chart-categorical-4)/20',
  NDC: 'bg-(--chart-categorical-5)/10 text-(--chart-categorical-5) ring-(--chart-categorical-5)/20',
  SNOMED: 'bg-(--chart-categorical-6)/10 text-(--chart-categorical-6) ring-(--chart-categorical-6)/20',
  'SNOMED CT': 'bg-(--chart-categorical-6)/10 text-(--chart-categorical-6) ring-(--chart-categorical-6)/20',
};

function normalizeInlineSystemKey(raw: string): string {
  const u = raw.toUpperCase().replace(/\s+/g, ' ');
  if (u === 'ICD10' || u === 'ICD-10' || u === 'ICD-10-CM') return 'ICD-10';
  if (u === 'RXNORM') return 'RxNorm';
  if (u === 'LOINC') return 'LOINC';
  if (u === 'CPT') return 'CPT';
  if (u === 'NDC') return 'NDC';
  if (u === 'SNOMED' || u === 'SNOMED CT') return 'SNOMED CT';
  return raw;
}

function inlineChipAbbrev(system: string): string {
  if (system === 'ICD-10') return 'Dx';
  if (system === 'RxNorm') return 'Rx';
  if (system === 'LOINC') return 'Lx';
  if (system === 'CPT') return 'CPT';
  if (system === 'NDC') return 'NDC';
  if (system === 'SNOMED CT') return 'Sn';
  return system;
}

function externalUrlForInlineSystem(system: string, code: string): string {
  if (system === 'NDC') {
    return `https://dailymed.nlm.nih.gov/dailymed/search.cfm?label=${encodeURIComponent(code)}`;
  }
  if (system === 'SNOMED CT') {
    return `https://browser.ihtsdotools.org/?perspective=full&conceptId1=${code}`;
  }
  return getCodeReferenceExternalUrl(system, code);
}

const SYSTEM_META: Record<string, { accent: string; label: string; site: string }> = {
  'ICD-10':    { accent: 'bg-blue-500',    label: 'Diagnosis',        site: 'ICD Codes' },
  RxNorm:      { accent: 'bg-emerald-500', label: 'Medication',       site: 'RxNav' },
  LOINC:       { accent: 'bg-violet-500',  label: 'Laboratory / Test', site: 'LOINC.org' },
  CPT:         { accent: 'bg-amber-500',   label: 'Procedure',        site: 'AAPC' },
  NDC:         { accent: 'bg-teal-500',    label: 'Drug Product',     site: 'DailyMed' },
  'SNOMED CT': { accent: 'bg-rose-500',    label: 'Clinical Finding', site: 'SNOMED Browser' },
};

function ClinicalTooltipBody({ system, code, term, url }: { system: string; code: string; term?: string; url: string }) {
  const meta = SYSTEM_META[system] ?? { accent: 'bg-slate-500', label: system, site: system };
  return (
    <div className="flex gap-2.5 min-w-[200px]">
      <div className={`${meta.accent} w-1 rounded-full shrink-0`} />
      <div className="flex flex-col gap-1 py-0.5">
        <span className="text-[10px] uppercase tracking-widest font-medium text-slate-400">{meta.label}</span>
        <span className="font-mono text-sm font-medium text-white tracking-wide">{code}</span>
        {term ? (
          <span className="text-[12px] text-slate-300 leading-snug line-clamp-3">{term}</span>
        ) : null}
        <span className="text-[10px] text-slate-500 mt-0.5">Standard: {system}</span>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-medium text-sky-400 hover:text-sky-300 transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="underline underline-offset-2">Open on {meta.site}</span>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3">
            <path d="M6.22 8.72a.75.75 0 0 0 1.06 1.06l5.22-5.22v1.69a.75.75 0 0 0 1.5 0v-3.5a.75.75 0 0 0-.75-.75h-3.5a.75.75 0 0 0 0 1.5h1.69L6.22 8.72Z" />
            <path d="M3.5 6.75c0-.69.56-1.25 1.25-1.25H7A.75.75 0 0 0 7 4H4.75A2.75 2.75 0 0 0 2 6.75v4.5A2.75 2.75 0 0 0 4.75 14h4.5A2.75 2.75 0 0 0 12 11.25V9a.75.75 0 0 0-1.5 0v2.25c0 .69-.56 1.25-1.25 1.25h-4.5c-.69 0-1.25-.56-1.25-1.25v-4.5Z" />
          </svg>
        </a>
      </div>
    </div>
  );
}

function InlineVocabularyChip({ rawLabel, code }: { rawLabel: string; code: string }) {
  const system = normalizeInlineSystemKey(rawLabel);
  const color = INLINE_CODE_COLORS[rawLabel.toUpperCase()] || INLINE_CODE_COLORS[system] || 'bg-surface-sunken text-text-tertiary';
  const url = externalUrlForInlineSystem(system, code);
  return (
    <TooltipPrimitive.Root delayDuration={180}>
      <TooltipTrigger asChild>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          title={`${system}: ${code}`}
          className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset ${color} not-prose cursor-pointer no-underline border-b border-dotted border-current/40 hover:brightness-110 transition-all`}
        >
          <span className="font-mono">{code}</span>
          <span className="opacity-70 text-[10px] font-medium">{inlineChipAbbrev(system)}</span>
        </a>
      </TooltipTrigger>
      <TooltipContent side="top" variant="clinical" className="max-w-xs text-left p-3">
        <ClinicalTooltipBody system={system} code={code} url={url} />
      </TooltipContent>
    </TooltipPrimitive.Root>
  );
}

export function renderInlineCodes(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const re = new RegExp(INLINE_CODE_RE.source, 'gi');
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const rawLabel = match[1];
    const code = match[2];
    parts.push(<InlineVocabularyChip key={`${match.index}-${code}`} rawLabel={rawLabel} code={code} />);
    lastIndex = re.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : [text];
}

export const MARKDOWN_PROSE_CLASS =
  'prose prose-sm dark:prose-invert max-w-none leading-relaxed ' +
  'prose-headings:font-medium prose-headings:text-foreground prose-headings:tracking-tight ' +
  'prose-h2:text-[15px] prose-h2:mt-6 prose-h2:mb-2.5 prose-h2:pb-1.5 prose-h2:border-b prose-h2:border-border/25 ' +
  'prose-h3:text-[14px] prose-h3:mt-4 prose-h3:mb-1.5 prose-h3:text-foreground/90 ' +
  'prose-hr:my-6 prose-hr:border-border/40 ' +
  'prose-p:text-[14px] prose-p:text-foreground/85 prose-p:leading-[1.8] prose-p:my-2.5 ' +
  'prose-strong:text-foreground prose-strong:font-medium prose-em:text-foreground/80 ' +
  'prose-code:text-[color:var(--chart-categorical-2)] prose-code:bg-surface-sunken prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md prose-code:text-xs prose-code:font-medium ' +
  'prose-pre:bg-surface-sunken prose-pre:border prose-pre:border-border prose-pre:rounded prose-pre:my-4 ' +
  'prose-ul:my-2.5 prose-ol:my-2.5 prose-li:my-1.5 prose-li:text-[14px] prose-li:leading-[1.75] prose-li:marker:text-primary/60 prose-li:pl-0.5 ' +
  'prose-table:my-3 prose-table:text-sm prose-th:px-3.5 prose-th:py-2.5 prose-th:font-medium prose-th:text-[11px] prose-th:uppercase prose-th:tracking-[0.08em] prose-th:text-text-tertiary prose-th:border-b prose-th:border-border/60 ' +
  'prose-td:px-3.5 prose-td:py-2.5 prose-td:border-b prose-td:border-border/40 prose-td:align-top ' +
  'prose-blockquote:border-l-brand prose-blockquote:bg-surface-sunken prose-blockquote:rounded-r-lg prose-blockquote:py-1 prose-blockquote:not-italic prose-blockquote:my-4';

/** Additional chart-briefing-specific prose styles for clean clinical dashboard styling. */
export const CHART_BRIEFING_PROSE_CLASS =
  MARKDOWN_PROSE_CLASS +
  ' prose-headings:text-foreground prose-headings:uppercase prose-headings:tracking-wide ' +
  'prose-h1:text-base prose-h1:font-medium prose-h1:mb-1.5 prose-h1:text-slate-50 ' +
  'prose-h2:text-[12px] prose-h2:uppercase prose-h2:font-medium prose-h2:tracking-wide prose-h2:text-sky-200 prose-h2:mt-3 prose-h2:mb-1.5 prose-h2:pb-1 ' +
  'prose-p:text-[14px] prose-p:text-foreground/90 prose-p:leading-[1.8] prose-p:my-2 ' +
  'prose-em:text-foreground/80 prose-em:italic prose-em:text-sm ' +
  'prose-ul:space-y-1.5 prose-ul:pl-1 prose-ol:space-y-1.5 prose-ol:pl-1 ' +
  'prose-li:text-[14px] prose-li:leading-[1.75] prose-li:text-slate-100 prose-li:marker:text-slate-400 prose-li:pl-0.5 ' +
  'prose-strong:text-slate-50 prose-strong:font-medium ' +
  'prose-h2:border-b prose-h2:border-border/25 prose-h2:pb-1.5';

export function buildClinicalMarkdownComponents(
  anchorPrefix: string,
  codeReferences: CodeRef[] | undefined,
): Record<string, React.ComponentType<Record<string, unknown>>> {
  return {
    h2: ({ children, ...props }: React.ComponentPropsWithoutRef<'h2'>) => (
      <h2 {...props} className="scroll-mt-24">
        {children}
      </h2>
    ),
    h3: ({ children, ...props }: React.ComponentPropsWithoutRef<'h3'>) => (
      <h3 {...props} className="scroll-mt-24">
        {children}
      </h3>
    ),
    hr: (props: React.ComponentPropsWithoutRef<'hr'>) => (
      <hr {...props} className="my-6 border-border/40" />
    ),
    a: ({ href, children, ...props }: React.ComponentPropsWithoutRef<'a'>) => {
      if (typeof href === 'string' && href.startsWith('#code-ref-')) {
        const anchorId = href.slice(1);
        const tail = anchorId.split('-').pop();
        const refIdx = tail ? Number(tail) : NaN;
        const ref = codeReferences?.[refIdx - 1];
        const chipClass =
          'inline-flex items-center justify-center min-w-4 text-[9px] font-medium ml-0.5 align-super rounded-full px-0.5 ' +
          'bg-slate-700/90 text-slate-100 hover:bg-slate-600 ring-1 ring-inset ring-slate-500/60 ' +
          'transition-colors cursor-pointer not-prose leading-none py-0.5 no-underline';
        const refSystem = ref?.system ?? '';
        const refCode = ref?.code ?? '';
        const refTerm = ref?.term ?? '';
        const trigger = ref ? (
          <a
            {...props}
            href={getCodeReferenceExternalUrl(refSystem, refCode)}
            target="_blank"
            rel="noopener noreferrer"
            title={`Open ${refSystem} ${refCode}`}
            className={chipClass}
          >
            {children}
          </a>
        ) : (
          <span
            {...props}
            className={
              chipClass +
              ' cursor-default opacity-70'
            }
          >
            {children}
          </span>
        );
        if (!ref || !Number.isFinite(refIdx)) {
          return trigger;
        }
        const url = getCodeReferenceExternalUrl(refSystem, refCode);
        return (
          <TooltipPrimitive.Root delayDuration={180}>
            <TooltipTrigger asChild>{trigger}</TooltipTrigger>
            <TooltipContent side="top" variant="clinical" className="max-w-xs text-left p-3">
              <ClinicalTooltipBody system={refSystem} code={refCode} term={refTerm} url={url} />
            </TooltipContent>
          </TooltipPrimitive.Root>
        );
      }
      return (
        <a {...props} href={href} className="text-primary underline underline-offset-2">
          {children}
        </a>
      );
    },
    sup: ({ children, ...props }: React.ComponentPropsWithoutRef<'sup'>) => {
      const dataRef = (props as Record<string, unknown>)['data-ref'];
      if (dataRef && codeReferences?.length) {
        const refIdx = Number(dataRef);
        const ref = codeReferences[refIdx - 1];
        const refSystem = ref?.system ?? '';
        const refCode = ref?.code ?? '';
        const refTerm = ref?.term ?? '';
        const url = ref ? getCodeReferenceExternalUrl(refSystem, refCode) : undefined;
        const accentBg = ref ? (SYSTEM_META[refSystem]?.accent ?? 'bg-slate-500') : 'bg-slate-600';
        const trigger =
          ref && url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              title={`${refSystem}: ${refCode}`}
              className={`inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full
                text-[9px] font-medium ml-0.5 align-super
                ${accentBg} text-white hover:brightness-110
                ring-1 ring-inset ring-white/20
                transition-all cursor-pointer not-prose no-underline shadow-sm`}
            >
              {refIdx}
            </a>
          ) : (
            <span
              className="inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full
                text-[9px] font-medium ml-0.5 align-super bg-slate-600 text-slate-300
                ring-1 ring-inset ring-slate-500/60 cursor-default opacity-70 not-prose"
            >
              {refIdx}
            </span>
          );
        if (!ref) {
          return <sup {...props}>{trigger}</sup>;
        }
        return (
          <sup {...props} className="not-prose">
            <TooltipPrimitive.Root delayDuration={180}>
              <TooltipTrigger asChild>{trigger}</TooltipTrigger>
              <TooltipContent side="top" variant="clinical" className="max-w-xs text-left p-3">
                <ClinicalTooltipBody system={refSystem} code={refCode} term={refTerm} url={url!} />
              </TooltipContent>
            </TooltipPrimitive.Root>
          </sup>
        );
      }
      return <sup {...props}>{children}</sup>;
    },
    strong: ({ children, ...props }: React.ComponentPropsWithoutRef<'strong'>) => {
      const childStr = typeof children === 'string' ? children : '';
      if (childStr && INLINE_CODE_RE.test(childStr)) {
        INLINE_CODE_RE.lastIndex = 0;
        return <strong {...props}>{renderInlineCodes(childStr)}</strong>;
      }
      return <strong {...props}>{children}</strong>;
    },
    p: ({ children, ...props }: React.ComponentPropsWithoutRef<'p'>) => {
      const mapped = React.Children.map(children, (child) => {
        if (typeof child === 'string' && INLINE_CODE_RE.test(child)) {
          INLINE_CODE_RE.lastIndex = 0;
          return <>{renderInlineCodes(child)}</>;
        }
        return child;
      });
      return <p {...props} className="whitespace-pre-wrap">{mapped}</p>;
    },
    em: ({ children, ...props }: React.ComponentPropsWithoutRef<'em'>) => (
      <em {...props} className="text-foreground/80 italic">{children}</em>
    ),
    // #186: Tailwind preflight strips default list markers, and these
    // overrides used to set spacing only, so nested items rendered with no
    // bullet glyphs at any depth. Depth-distinct glyphs per rule 16: level 1
    // filled disc, level 2+ hollow circle (the `[&_ul]:` variant outranks the
    // nested element's own `list-disc` by specificity). Marker color rides
    // the theme-aware foreground token so it reads in light and dark mode.
    ul: ({ children, ...props }: React.ComponentPropsWithoutRef<'ul'>) => (
      <ul
        {...props}
        className="list-disc list-outside space-y-1.5 pl-5 marker:text-foreground/50 [&_ul]:list-[circle] [&_ul]:mt-1.5"
      >
        {children}
      </ul>
    ),
    ol: ({ children, ...props }: React.ComponentPropsWithoutRef<'ol'>) => (
      <ol
        {...props}
        className="list-decimal list-outside space-y-1.5 pl-5 marker:text-foreground/50"
      >
        {children}
      </ol>
    ),
    li: ({ children, ...props }: React.ComponentPropsWithoutRef<'li'>) => {
      const mapped = React.Children.map(children, (child) => {
        if (typeof child === 'string' && INLINE_CODE_RE.test(child)) {
          INLINE_CODE_RE.lastIndex = 0;
          return <>{renderInlineCodes(child)}</>;
        }
        return child;
      });
      return <li {...props} className="pl-0.5">{mapped}</li>;
    },
    table: ({ children, ...props }: React.ComponentPropsWithoutRef<'table'>) => (
      <div className="my-3 overflow-x-auto rounded-lg border border-border/70 bg-background not-prose">
        <table {...props} className="w-full border-collapse text-sm">{children}</table>
      </div>
    ),
    thead: ({ children, ...props }: React.ComponentPropsWithoutRef<'thead'>) => (
      <thead {...props} className="border-b border-border/70">{children}</thead>
    ),
    tbody: ({ children, ...props }: React.ComponentPropsWithoutRef<'tbody'>) => (
      <tbody {...props}>{children}</tbody>
    ),
    th: ({ children, ...props }: React.ComponentPropsWithoutRef<'th'>) => (
      <th {...props} className="px-3 py-2.5 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">{children}</th>
    ),
    td: ({ children, ...props }: React.ComponentPropsWithoutRef<'td'>) => {
      const mapped = React.Children.map(children, (child) => {
        if (typeof child === 'string' && INLINE_CODE_RE.test(child)) {
          INLINE_CODE_RE.lastIndex = 0;
          return <>{renderInlineCodes(child)}</>;
        }
        return child;
      });
      return <td {...props} className="px-3 py-2.5 text-[13px] text-foreground border-b border-border/35 whitespace-pre-wrap">{mapped}</td>;
    },
  };
}

/** Markdown with optional code-reference superscripts + inline vocabulary chips (context from CodeReferencesUiContext). */
export function ClinicalMarkdownBody({
  content,
  className,
}: {
  content: string;
  className?: string;
}) {
  const ctx = useContext(CodeReferencesUiContext);
  const refs = ctx?.refs ?? [];
  const scopeId = ctx?.scopeId ?? '';
  const anchorPrefix = scopeId ? `code-ref-${scopeId}-` : 'code-ref-';
  const refSig = refs.map((r) => `${r.system}\t${r.code}\t${r.term}`).join('\n');
  const processed = useMemo(
    () => (refs.length && scopeId ? injectCodeMarkers(content, refs, scopeId) : content),
    [content, refSig, refs.length, scopeId],
  );
  const components = useMemo(
    () => buildClinicalMarkdownComponents(anchorPrefix, refs.length ? refs : undefined),
    [anchorPrefix, refSig, refs.length],
  );
  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex, [rehypeSanitize, clinicalMarkdownSanitizeSchema]]}
        components={components as Record<string, React.ComponentType<Record<string, unknown>>>}
      >
        {processed}
      </ReactMarkdown>
    </div>
  );
}
